import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';

/**
 * Derives a safe filename from a document URL (e.g. signed S3 or answers path).
 * @param {string} url - Document URL
 * @param {number} index - Fallback index for default name
 * @returns {string}
 */
export const getFilenameFromUrl = (url, index = 0) => {
  const match = url?.match(/answers\/([^?/]+)(?:\?|$)/) || url?.match(/\/([^/]+?)(?:\?|$)/);
  return match ? match[1] : `document-${index + 1}`;
};

// Extract file extension from URL or filename
export const getFileExtensionFromUrl = url => {
  try {
    const filename = url.split('/').pop().split('?')[0];
    return filename.split('.').pop().toLowerCase() || 'file';
  } catch {
    return 'file';
  }
};

/**
 * Fetches multiple URLs as binary, zips them, and triggers a download.
 * @param {string[]} urls - Array of document URLs
 * @param {string} zipName - Output zip filename (default 'documents.zip')
 * @param {{ onStart?: () => void, onEnd?: () => void }} options - Optional callbacks for loading state
 */
export const downloadAllAsZip = async (
  urls,
  zipName = 'documents.zip',
  { onStart, onEnd } = {},
) => {
  if (!urls?.length) return;
  onStart?.();
  try {
    const zip = new JSZip();
    const results = await Promise.all(
      urls.map(
        (url, index) =>
          new Promise((resolve, reject) => {
            JSZipUtils.getBinaryContent(url, (err, data) => {
              if (err) reject(err);
              else resolve({ name: getFilenameFromUrl(url, index), data });
            });
          }),
      ),
    );
    results.forEach(({ name, data }, index) => {
      zip.file(`${index + 1}. ${name}`, data, { binary: true });
    });
    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, zipName);
  } catch (e) {
    console.error('Failed to create zip:', e);
  } finally {
    onEnd?.();
  }
};
